import React from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { ThemeProvider as LegacyThemeProvider } from '@mui/styles';
import { ApolloProvider } from '../reactApolloCompat';
import { unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';
import { SearchTermProvider } from '../context/SearchTerm';
import { SnackbarMessageProvider } from '../context/SnackbarMessage';
import { getClient } from '../utils/apollo';
import history from '../utils/history';
import { EditAppointmentProvider } from '../context/EditAppointment';

const theme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1536,
            xxl: 1920
        }
    },
    palette: {
        action: {
            active: '#413BBE',
            hover: '#EFF8FD',
            selected: '#E1F0FB',
            save: '#14A554',
            destroy: '#bb5a5a'
        },
        text: {
            secondary: 'rgba(0, 0, 0, 0.54)'
        },
        primary: {
            main: '#7d9cc4',
            light: '#a1bbdc'
        },
        secondary: {
            main: '#e64040'
        },
        validation: {
            error: '#FF0000', // is empty when required, or bad data
            suggested: '#FFA500', // is empty when suggested
            optional: '#990CFF', // highlight changes for approval
            good: '#14A554'
        }
    },
    sizes: {
        headerHeight: 72,
        footerHeight: 80,
        drawerWidth: 250,
        tasksDrawerWidth: 350,
        drawerWidthMinimized: 50,
        tabHeight: 56
    },
    typography: {
        h1: { lineHeight: 1, color: '#333' },
        h2: { lineHeight: 1, color: '#333' },
        h3: { lineHeight: 1.04, color: '#333' },
        h4: { lineHeight: 1.17, color: '#333' },
        h5: { lineHeight: 1.33, color: '#333' },
        h6: { letterSpacing: 'normal', color: '#333' },
        subtitle1: { color: '#333' },
        subtitle2: { color: '#333' },
        body1: { lineHeight: 1.46429, letterSpacing: 'normal', color: '#333' },
        body2: { fontWeight: 500, lineHeight: 1.71429, letterSpacing: 'normal', color: '#333' },
        button: { lineHeight: 'normal', letterSpacing: 'normal', color: '#333' },
        caption: { lineHeight: 1.375, letterSpacing: 'normal', color: 'rgba(0, 0, 0, 0.54)' },
        overline: { color: '#333' },
        fontSizes: { regular: '14px' }
    },
    shadows: [
        'none',
        '0px 1px 3px 0px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 2px 1px -1px rgba(0,0,0,0.12)',
        '0px 1px 5px 0px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 3px 1px -2px rgba(0,0,0,0.12)',
        '0px 1px 8px 0px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 3px 3px -2px rgba(0,0,0,0.12)',
        '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
        '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
        '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
        '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
        '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
        '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
        '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
        '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
        '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
        '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
        '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
        '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
        '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
        '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
        '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
        '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
        '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
        '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
        '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
        '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
        '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)'
    ],
    components: {
        MuiGrid: {
            styleOverrides: {
                container: ({ ownerState, theme }) => {
                    const s = ownerState && ownerState.spacing;
                    if (!s) return {};
                    const half = theme.spacing(Number(s) / 2);
                    return {
                        margin: 0,
                        width: '100%',
                        '& > .MuiGrid-item': {
                            padding: half
                        }
                    };
                }
            }
        },
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    lineHeight: 'normal',
                    letterSpacing: 'normal',
                    color: 'inherit'
                },
                'strong, b': { fontWeight: 500, lineHeight: 'normal' },
                pre: { lineHeight: 'normal' },
                'h1, h2, h3, h4, h5, h6': { lineHeight: 'normal' }
            }
        },
        MuiTypography: {
            defaultProps: { variant: 'body2' },
            styleOverrides: {
                root: { display: 'block' },
                body1: { fontSize: '0.875rem', letterSpacing: '0.00938em' },
                body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5, letterSpacing: '0.01071em' }
            }
        },
        MuiFormControl: {
            defaultProps: { variant: 'standard' }
        },
        MuiTextField: {
            defaultProps: { variant: 'standard' }
        },
        MuiSelect: {
            defaultProps: { variant: 'standard' },
            styleOverrides: { select: { minHeight: '1.1875em', letterSpacing: '0.3px' } }
        },
        MuiFormLabel: {
            styleOverrides: { root: { lineHeight: 1 } }
        },
        MuiFormHelperText: {
            styleOverrides: { root: { lineHeight: 1, fontSize: '0.75rem', letterSpacing: '0.3px' } }
        },
        MuiInputLabel: {
            styleOverrides: {
                root: { letterSpacing: '0.3px' },
                shrink: { transform: 'translate(0, 1.5px) scale(0.75)' },
                formControl: { transform: 'translate(0, 24px) scale(1)' }
            }
        },
        MuiInputBase: {
            styleOverrides: {
                root: { fontSize: '1rem', lineHeight: '1.1875em' },
                // Scoped to date/time only: Chromium scales the native picker icon with the content box,
                // so applying this globally makes every input 4px taller.
                input: {
                    '&[type="date"], &[type="time"]': {
                        padding: '6px 0 7px',
                        height: '1.1875em',
                        boxSizing: 'content-box'
                    }
                }
            }
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    marginLeft: -14
                }
            }
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    padding: '12px 6px 12px 12px'
                }
            }
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    padding: 12
                }
            }
        },
        MuiListItem: {
            styleOverrides: {
                root: { paddingTop: 11, paddingBottom: 11 },
                dense: { paddingTop: 8, paddingBottom: 8 },
                button: { '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' } },
                secondaryAction: { paddingRight: 32 }
            }
        },
        MuiListItemIcon: {
            styleOverrides: { root: { marginRight: 16, minWidth: 'auto' } }
        },
        MuiListItemText: {
            styleOverrides: {
                root: { padding: '0 16px', marginTop: 0, marginBottom: 0, '&:first-of-type': { paddingLeft: 0 } },
                inset: { '&:first-of-type': { paddingLeft: 56 } },
                dense: { fontSize: '0.8125rem' }
            }
        },
        MuiTabScrollButton: {
            styleOverrides: {
                root: {
                    width: 56,
                    opacity: 1,
                    '&.Mui-disabled': { width: 0, opacity: 0 }
                }
            }
        },
        MuiTabs: {
            defaultProps: { textColor: 'inherit', indicatorColor: 'secondary' }
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    lineHeight: '35px',
                    letterSpacing: '0.02857em',
                    padding: '16px 34px',
                    minHeight: 67,
                    '@media (min-width:960px)': { fontSize: '0.8125rem', minWidth: 160 }
                },
                textColorInherit: {
                    opacity: 0.7,
                    '&.Mui-selected': { opacity: 1 }
                }
            }
        },
        MuiButton: {
            defaultProps: { color: 'inherit' },
            styleOverrides: {
                root: {
                    fontSize: '0.875rem',
                    lineHeight: 1.75,
                    textTransform: 'capitalize',
                    letterSpacing: '0.02857em'
                },
                colorInherit: { color: 'rgba(0, 0, 0, 0.87)' },
                text: { '&:hover': { backgroundColor: 'rgba(51, 51, 51, 0.08)' } },
                outlined: { padding: '5px 16px', border: '1px solid rgba(0, 0, 0, 0.26)' }
            }
        },
        MuiSvgIcon: { styleOverrides: { root: { fontSize: 24 } } },
        MuiIconButton: {
            styleOverrides: {
                sizeLarge: { fontSize: '1.5rem' }
            }
        },
        MuiIcon: { styleOverrides: { root: { fontSize: 24 } } },
        MuiMenuItem: { styleOverrides: { root: { fontSize: '0.875rem' } } },
        MuiTableRow: {
            styleOverrides: {
                root: { height: 48 },
                head: { height: 56 }
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.46429 },
                body: { paddingTop: 4, paddingBottom: 4 },
                head: { fontSize: '0.75rem', fontWeight: 500, color: 'rgba(0, 0, 0, 0.54)' }
            }
        },
        MuiTableSortLabel: {
            styleOverrides: { icon: { fontSize: 16 } }
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: { padding: '0 24px' },
                content: { '& > :last-child': { paddingRight: 32 } },
                expandIconWrapper: {
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%) rotate(0deg)',
                    marginRight: 0,
                    '&.Mui-expanded': { transform: 'translateY(-50%) rotate(180deg)' }
                }
            }
        },
        MuiPaper: {
            defaultProps: { elevation: 2 },
            styleOverrides: { root: { color: 'inherit' } }
        },
        MuiDialog: {
            styleOverrides: {
                paper: { margin: 48 }
            }
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    padding: '24px 24px 20px',
                    letterSpacing: '0.0075em',
                    color: 'rgba(0, 0, 0, 0.87)'
                }
            }
        },
        MuiDialogContent: {
            styleOverrides: {
                root: { padding: '0 24px 24px' }
            }
        },
        MuiDialogContentText: {
            styleOverrides: {
                root: { fontSize: '1rem', lineHeight: '24px', letterSpacing: '0.00938em' }
            }
        },
        MuiDialogActions: {
            styleOverrides: {
                root: { padding: 0, margin: '8px 4px' },
                // Neutralize v5's auto gap between adjacent action buttons.
                spacing: { '& > :not(:first-of-type)': { marginLeft: 0 } }
            }
        },
        MuiTooltip: {
            styleOverrides: {
                popper: { opacity: 0.9, willChange: 'transform' },
                tooltip: {
                    fontSize: 10,
                    lineHeight: '14px',
                    fontWeight: 400,
                    margin: '0 14px'
                }
            }
        },
        MuiInputAdornment: {
            styleOverrides: { root: { color: 'rgba(0, 0, 0, 0.87)' } }
        },
        // Override typography.button letterSpacing: 'normal' for Fab only,
        // where the extra spacing is needed so avatar initials don't render narrower.
        MuiFab: {
            styleOverrides: { root: { letterSpacing: '0.02857em', lineHeight: 1.75 } }
        }
    }
});

const client = getClient();

export default ({ children }) => (
    <SnackbarMessageProvider>
        <SearchTermProvider>
            <EditAppointmentProvider>
                <ApolloProvider client={client}>
                    <HistoryRouter history={history}>
                        <StyledEngineProvider injectFirst>
                            <ThemeProvider theme={theme}>
                                <LegacyThemeProvider theme={theme}>
                                    <CssBaseline />
                                    {children}
                                </LegacyThemeProvider>
                            </ThemeProvider>
                        </StyledEngineProvider>
                    </HistoryRouter>
                </ApolloProvider>
            </EditAppointmentProvider>
        </SearchTermProvider>
    </SnackbarMessageProvider>
);
